/**
 * Receipt template runtime — SHARED VERBATIM between RedFireISE
 * (src/app/interfaces/receipt-template/) and Quartz
 * (src/app/modules/report/receipt-template-editor/). Keep the two copies
 * byte-identical: ISE renders the printed receipt with it, Quartz renders the
 * editor preview with it, and the preview is only honest while both run the
 * same code. Changes must stay additive — devices in the field run older
 * copies than the editor.
 *
 * No Angular, no globals: everything the template needs is passed in.
 *
 * A template receives these locals:
 *   receipt   – the ReceiptModel below (pre-flattened purchase data)
 *   h         – ReceiptHelpers (fixed-width text helpers + formatters)
 *   purchase, location, client, user – the raw documents, as escape hatches
 */

export const RECEIPT_RUNTIME_VERSION = 3;

/**
 * Short order number for calling out and matching orders: the last four digits
 * of the check number (a twelve-digit random key nobody can read off a ticket).
 * Printed on the customer receipt, the kitchen ticket and the kitchen display,
 * so a sandwich can be matched to its receipt at a glance. Empty when the key
 * has no digits.
 */
export function orderNumberFromKey(key: any): string {
  const digits = (key === undefined || key === null ? '' : String(key)).replace(/\D/g, '');
  return digits.length > 0 ? digits.slice(-4) : '';
}
export const RECEIPT_DEFAULT_WIDTH = 35;

/** Payment transaction types that print as tender lines. */
export const RECEIPT_PAYMENT_TYPES = ['cash', 'credit', 'gift', 'check', 'billingAccount'];

export interface ReceiptItemField {
  path: string | string[];
  style?: string;
  separator?: string;
}

export interface ReceiptModelInput {
  purchase: any;
  location: any;
  /** The client's Configuration.settings (showItemCount, ...). Optional. */
  settings?: any;
  client?: any;
  user?: any;
  /** Overrides purchase.transactions when supplied. */
  transactions?: any[];
  /** clientSettings.settings.iseItem.fields — drives the item line text. */
  itemFields?: ReceiptItemField[];
  /** Inventory record for a top-level purchase line (ISE: item.account). */
  getAccount?: (item: any) => any;
  width?: number;
}

export interface ReceiptLine {
  depth: number;
  qty: number;
  qtyText: string;
  name: string;
  /** Item text as configured by the client's item fields (name + SKU, etc.). */
  text: string;
  /** Unit price in cents (null when the line has no price). */
  price: number | null;
  /** Extended price in cents (unit × qty × parent multiplier). */
  extended: number | null;
  /** Legacy right-hand column text ('' or '  $x.xx'). */
  priceText: string;
  options: ReceiptOptionGroup[];
  discounts: Array<{ name: string; amount: number }>;
  notes: string[];
  /** Fully formatted lines for this item (options, discounts, notes included). */
  lines: string[];
}

export interface ReceiptOptionGroup {
  name: string;
  options: ReceiptLine[];
}

export interface ReceiptPayment {
  type: string;
  label: string;
  ledgerName: string;
  cardBrand: string;
  lastFour: string;
  checkNum: string;
  accountName: string;
  amount: number;
  change: number;
  tendered: number | null;
  rounding: number;
}

export interface ReceiptModel {
  width: number;
  client: { name: string; nameExtended: string; logoUrl: string };
  location: {
    name: string;
    clientName: string;
    clientNameExtended: string;
    address: { streetAddress: string; streetAddressExt: string; addressLocality: string; addressRegion: string; postalCode: string };
    cityStateZip: string;
    telephone: string;
    telephoneFormatted: string;
    website: string;
    email: string;
  };
  purchase: {
    key: string;
    /** Last four digits of the check number — see orderNumberFromKey. */
    orderNumber: string;
    type: string;
    typeName: string;
    status: string;
    isComplete: boolean;
    date: Date;
    dateString: string;
    customerName: string;
    userName: string;
    notes: string[];
  };
  items: ReceiptLine[];
  discounts: Array<{ name: string; amount: number }>;
  totals: {
    subtotal: number; discount: number; tax: number; tip: number; total: number; due: number; rounding: number;
    /** Tax a marketplace facilitator (DoorDash) collected instead of the register, with its label; 0 / '' otherwise. */
    facilitatorTax: number; facilitatorTaxLabel: string;
  };
  /** Total item count — see countReceiptItems. */
  itemCount: number;
  /** The client setting showItemCount (default off): print an Items line. */
  showItemCount: boolean;
  payments: ReceiptPayment[];
  hasTransactions: boolean;
  fulfillments: Array<{ lastFour: string; amount: number }>;
  footer: Array<{ text: string; style: string }> | null;
}

export interface ReceiptHelpers {
  width: number;
  /** Left text and right text on one line; wraps the left text when needed. */
  line(left: string, right?: string): string;
  center(text: string): string;
  left(text: string): string;
  right(text: string): string;
  /** A full-width rule of one character. */
  repeat(ch: string): string;
  /** Word-wrap to the receipt width; returns the lines. */
  wrap(text: string): string[];
  /** Apply a legacy footer style name: center | left | right | repeat. */
  style(text: string, style: string): string;
  /** Cents → '$1.23' (negative → '$-1.23', matching the legacy receipt). */
  money(cents: number): string;
  /** Cents → '1.23' without the currency symbol. */
  amount(cents: number): string;
  dateTime(value: any): string;
  date(value: any): string;
  time(value: any): string;
  phone(value: string): string;
  upper(value: any): string;
  lower(value: any): string;
  truncate(value: any, length: number): string;
  padLeft(value: any, length: number, ch?: string): string;
  padRight(value: any, length: number, ch?: string): string;
}

const str = (value: any): string => (value === undefined || value === null ? '' : String(value));

const spaces = (count: number): string => (count > 0 ? ' '.repeat(count) : '');

/** Word-wrap text to width; words longer than the width are split by character. */
export function wrapText(text: string, width: number): string[] {
  const out: string[] = [];
  for (const paragraph of str(text).split('\n')) {
    const words = paragraph.split(' ').filter(w => w.length > 0);
    if (words.length === 0) {
      out.push('');
      continue;
    }
    let current = '';
    for (let word of words) {
      while (word.length > width) {
        if (current.length > 0) {
          out.push(current);
          current = '';
        }
        out.push(word.substr(0, width));
        word = word.substr(width);
      }
      if (current.length === 0) {
        current = word;
      } else if (current.length + 1 + word.length <= width) {
        current += ' ' + word;
      } else {
        out.push(current);
        current = word;
      }
    }
    if (current.length > 0) {
      out.push(current);
    }
  }
  return out;
}

export function centerText(text: string, width: number): string {
  const value = str(text);
  const free = width - value.length;
  if (free <= 0) {
    return value;
  }
  const lead = Math.ceil(free / 2);
  return spaces(lead) + value + spaces(width - lead - value.length);
}

/**
 * Two-column line. When both parts fit, the right text is pushed to the
 * margin. When they don't, the left text wraps over as many lines as needed
 * and its last word(s) share the final line with the right text — the layout
 * the legacy receipt used for long item names.
 */
export function splitLine(leftText: string, rightText: string, width: number): string {
  const leftValue = str(leftText);
  const rightValue = str(rightText);
  const free = width - leftValue.length - rightValue.length;

  if (free >= 0) {
    return leftValue + spaces(free) + rightValue;
  }

  const parts = leftValue.split(' ').filter(p => p.length > 0);

  if (parts.length <= 1) {
    // One unbreakable run of text: chunk it, right text on its own line.
    const lines = wrapText(leftValue, width);
    lines.push(spaces(width - rightValue.length) + rightValue);
    return lines.join('\n');
  }

  let lastWords = parts.length > 3 ? 2 : 1;
  let lastLine = parts.slice(parts.length - lastWords).join(' ');
  if (width - lastLine.length - rightValue.length <= 0) {
    lastWords = 1;
    lastLine = parts[parts.length - 1];
  }

  const lines = wrapText(parts.slice(0, parts.length - lastWords).join(' '), width);
  const gap = width - lastLine.length - rightValue.length;
  if (gap >= 0) {
    lines.push(lastLine + spaces(gap) + rightValue);
  } else {
    // Even a single word can't share a line with the right text.
    lines.push(...wrapText(lastLine, width));
    lines.push(spaces(width - rightValue.length) + rightValue);
  }
  return lines.join('\n');
}

export function formatMoney(cents: number): string {
  const value = Number(cents);
  return '$' + (isNaN(value) ? 0 : value / 100).toFixed(2);
}

export function formatPhone(value: string): string {
  const raw = str(value);
  let digits = raw.replace(/\D/g, '');
  if (digits.length === 11 && digits.charAt(0) === '1') {
    digits = digits.substr(1);
  }
  if (digits.length !== 10) {
    return raw;
  }
  return '(' + digits.substr(0, 3) + ') ' + digits.substr(3, 3) + '-' + digits.substr(6, 4);
}

const toDate = (value: any): Date | null => {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  return isNaN(date.getTime()) ? null : date;
};

export function createReceiptHelpers(width: number = RECEIPT_DEFAULT_WIDTH): ReceiptHelpers {
  const helpers: ReceiptHelpers = {
    width,
    line: (left: string, right: string = '') => splitLine(left, right, width),
    center: (text: string) => centerText(text, width),
    left: (text: string) => wrapText(text, width).join('\n'),
    right: (text: string) => wrapText(text, width).map(l => spaces(width - l.length) + l).join('\n'),
    repeat: (ch: string) => str(ch).substr(0, 1).repeat(width),
    wrap: (text: string) => wrapText(text, width),
    style: (text: string, style: string) => {
      switch (style) {
        case 'center':
          return helpers.center(text);
        case 'right':
          return helpers.right(text);
        case 'repeat':
          return helpers.repeat(text);
        case 'left':
          return helpers.left(text);
        default:
          return str(text);
      }
    },
    money: (cents: number) => formatMoney(cents),
    amount: (cents: number) => formatMoney(cents).substr(1),
    dateTime: (value: any) => { const d = toDate(value); return d ? d.toLocaleString() : ''; },
    date: (value: any) => { const d = toDate(value); return d ? d.toLocaleDateString() : ''; },
    time: (value: any) => { const d = toDate(value); return d ? d.toLocaleTimeString() : ''; },
    phone: (value: string) => formatPhone(value),
    upper: (value: any) => str(value).toUpperCase(),
    lower: (value: any) => str(value).toLowerCase(),
    truncate: (value: any, length: number) => str(value).substr(0, length),
    padLeft: (value: any, length: number, ch: string = ' ') => {
      const s = str(value);
      return s.length >= length ? s : str(ch).substr(0, 1).repeat(length - s.length) + s;
    },
    padRight: (value: any, length: number, ch: string = ' ') => {
      const s = str(value);
      return s.length >= length ? s : s + str(ch).substr(0, 1).repeat(length - s.length);
    },
  };
  return helpers;
}

// ---- item line text (mirrors ISE shared/item/item-fields.ts) ----------------

function fieldValue(source: any, path: string, nameOverride?: string, firstArrayValue = false): string {
  if (!source || !path) { return ''; }
  if (path === 'name' && nameOverride) { return nameOverride; }
  const value = source[path];
  if (value === undefined || value === null) { return ''; }
  if (Array.isArray(value)) {
    if (firstArrayValue) {
      return value.length > 0 && value[0] !== undefined && value[0] !== null ? String(value[0]) : '';
    }
    return value.join(', ');
  }
  return String(value);
}

function itemFieldText(source: any, field: ReceiptItemField, nameOverride?: string, firstArrayValue = false): string {
  const paths = Array.isArray(field.path) ? field.path : [field.path];
  const parts = paths.map(p => fieldValue(source, p, nameOverride, firstArrayValue)).filter(v => v !== '');
  return parts.join(field.separator === undefined || field.separator === null ? ' ' : field.separator);
}

/**
 * The configured item data fields as one line (array fields limited to their
 * first value, as the printed receipt does). Falls back to name + unit
 * description when the client has no item field config.
 */
export function itemLineText(source: any, nameOverride: string, itemFields: ReceiptItemField[] | undefined, separator = ' '): string {
  const fields = (itemFields || []).filter(f => f && (
    typeof f.path === 'string' || (Array.isArray(f.path) && f.path.every(p => typeof p === 'string'))
  ));
  if (fields.length > 0) {
    const parts = fields.map(f => itemFieldText(source, f, nameOverride, true)).filter(v => v !== '');
    if (parts.length > 0) { return parts.join(separator); }
  }
  const name = nameOverride || fieldValue(source, 'name');
  const unit = fieldValue(source, 'unitDescription');
  return unit ? name + separator + unit : name;
}

// ---- item count -------------------------------------------------------------

/** Units of measure that mark a cart line as sold by weight. */
export const RECEIPT_WEIGHT_UNITS = ['lb', 'lbs', 'oz', 'kg', 'g', 'gram', 'grams', 'pound', 'pounds', 'ounce', 'ounces'];

/**
 * True for a cart line sold by weight: its inventory unit of measure is a
 * weight unit, or its quantity is fractional. Such a line counts as ONE item.
 */
export function isWeighedItem(item: any, account?: any): boolean {
  const unit = str(account && account.unitOfMeasure).trim().toLowerCase();
  if (unit && RECEIPT_WEIGHT_UNITS.indexOf(unit) > -1) {
    return true;
  }
  const qty = Number(item && item.qty);
  return isFinite(qty) && qty !== Math.trunc(qty);
}

/**
 * Total item count for a purchase: the sum of the top-level cart line
 * quantities, where a weighed line counts as one item whatever its weight.
 * Option/modifier lines are never counted. Quantities count by magnitude, so
 * a return reports how many items came back rather than a negative number.
 * Shared by the register's purchase view, the receipt model and the legacy
 * receipt so all three agree.
 */
export function countReceiptItems(items: any[], getAccount?: (item: any) => any): number {
  let count = 0;
  for (const item of items || []) {
    if (!item) {
      continue;
    }
    const account = getAccount ? getAccount(item) : item.account;
    if (isWeighedItem(item, account)) {
      count += 1;
      continue;
    }
    const qty = Math.abs(Number(item.qty));
    if (isFinite(qty)) {
      count += qty;
    }
  }
  return count;
}

// ---- model ------------------------------------------------------------------

function buildLine(item: any, multiplier: number, depth: number, input: ReceiptModelInput, h: ReceiptHelpers): ReceiptLine {
  const spacer = '  '.repeat(depth);
  const qty = Number(item.qty);
  const price = item.price ? Number(item.price) : null;

  // Legacy: the price column shows unit × qty × the parent multiplier, and the
  // parent multiplier only compounds when qty isn't exactly 1.
  let priceText = '';
  let extended: number | null = null;
  if (item.price) {
    extended = Number(item.price) * multiplier * qty;
    priceText = '  ' + h.money(extended);
  }

  let qtyText = '';
  let childMultiplier = multiplier;
  if (item.qty !== 1) {
    qtyText = item.qty + ' ';
    childMultiplier = multiplier * qty;
  }

  const account = depth === 0 && input.getAccount ? input.getAccount(item) : null;
  const text = account ? itemLineText(account, item.name, input.itemFields, ' ') : str(item.name);

  const lines: string[] = [h.line(spacer + qtyText + text, priceText)];

  const options: ReceiptOptionGroup[] = [];
  if (item.options) {
    for (const groupKey of Object.keys(item.options)) {
      const group = item.options[groupKey];
      if (!group || !(group.selected > 0)) {
        continue;
      }
      const selected: ReceiptLine[] = [];
      lines.push(h.line('  ' + spacer + str(group.name), ''));
      for (const optionKey of Object.keys(group.options || {})) {
        const option = group.options[optionKey];
        if (option && option.qty > 0) {
          const optionLine = buildLine(option, childMultiplier, depth + 2, input, h);
          selected.push(optionLine);
          lines.push(...optionLine.lines);
        }
      }
      options.push({ name: str(group.name), options: selected });
    }
  }

  const discounts: Array<{ name: string; amount: number }> = [];
  let hasDiscount = false;
  if (item.discountsUi) {
    for (const discountId of Object.keys(item.discountsUi)) {
      const displayOnItem = item.discountsUiDisplayOnItem && item.discountsUiDisplayOnItem[discountId] === true;
      if (!displayOnItem) {
        continue;
      }
      hasDiscount = true;
      const value = Number(item.discountsUi[discountId]);
      if (value >= 0.5 || value < -0.5) {
        const name = (item.discountsUiName && item.discountsUiName[discountId])
          || (input.purchase && input.purchase.discounts && input.purchase.discounts[discountId] && input.purchase.discounts[discountId].name)
          || 'Discount';
        discounts.push({ name: str(name), amount: -value });
        lines.push(h.line('  ' + spacer + str(name), '  $-' + (value / 100).toFixed(2)));
      }
    }
  }

  if ((item.options || hasDiscount) && depth === 0) {
    lines.push('');
  }

  const notes: string[] = depth === 0 && Array.isArray(item.note) ? item.note.map(n => str(n)) : [];
  for (const note of notes) {
    lines.push('   * ' + note);
  }

  return {
    depth,
    qty,
    qtyText,
    name: str(item.name),
    text,
    price,
    extended,
    priceText,
    options,
    discounts,
    notes,
    lines,
  };
}

export function buildReceiptModel(input: ReceiptModelInput): ReceiptModel {
  const width = input.width || RECEIPT_DEFAULT_WIDTH;
  const h = createReceiptHelpers(width);
  const purchase = input.purchase || {};
  const location = input.location || {};
  const client = input.client || {};
  const settings = input.settings || {};
  const address = location.address || {};
  const transactions: any[] = input.transactions || purchase.transactions || [];

  const cityStateZip = (str(address.addressLocality) + ', ' + str(address.addressRegion) + ' ' + str(address.postalCode)).trim();

  const items: ReceiptLine[] = (purchase.items || []).map((item: any) => buildLine(item, 1, 0, input, h));

  const discounts = transactions
    .filter(t => t && t.type === 'discount' && t.amount && !t.displayOnItem)
    .map(t => ({ name: str(t.name), amount: Number(t.amount) }));

  const payments: ReceiptPayment[] = transactions
    .filter(t => t && RECEIPT_PAYMENT_TYPES.indexOf(t.type) > -1)
    .map(t => {
      let label = t.ledgerName ? str(t.ledgerName).toUpperCase() : str(t.type).toUpperCase();
      if (t.cardBrand) {
        label = str(t.cardBrand).toUpperCase();
      }
      if (t.lastFour) {
        label += ' XXXX' + t.lastFour;
      }
      if (t.checkNum) {
        label += ' #' + t.checkNum;
      }
      const amount = parseInt(t.amount, 10) || 0;
      const change = parseInt(t.change, 10) || 0;
      return {
        type: str(t.type),
        label,
        ledgerName: str(t.ledgerName),
        cardBrand: str(t.cardBrand),
        lastFour: str(t.lastFour),
        checkNum: str(t.checkNum),
        accountName: str(t.accountName),
        amount,
        change,
        tendered: change > 0 ? amount - change : null,
        rounding: Number(t.rounding || purchase.rounding || 0),
      };
    });

  const date = toDate(purchase.timeUpdated) || new Date();

  let footer: Array<{ text: string; style: string }> | null = null;
  if (location.receipt && Array.isArray(location.receipt.footer)) {
    footer = location.receipt.footer
      .filter((f: any) => f && f.type === 'text')
      .map((f: any) => ({ text: str(f.text), style: str(f.style) }));
  }

  return {
    width,
    client: {
      name: str(client.name || location.clientName),
      nameExtended: str(client.nameExtended || location.clientNameExtended),
      logoUrl: str(client.logoUrl || location.logoUrl),
    },
    location: {
      name: str(location.name),
      clientName: str(location.clientName || client.name),
      clientNameExtended: str(location.clientNameExtended || client.nameExtended),
      address: {
        streetAddress: str(address.streetAddress),
        streetAddressExt: str(address.streetAddressExt),
        addressLocality: str(address.addressLocality),
        addressRegion: str(address.addressRegion),
        postalCode: str(address.postalCode),
      },
      cityStateZip,
      telephone: str(location.telephone),
      telephoneFormatted: location.telephone ? formatPhone(location.telephone) : '',
      website: str(location.website),
      email: str(location.email),
    },
    purchase: {
      key: str(purchase.key),
      orderNumber: orderNumberFromKey(purchase.key),
      type: str(purchase.type),
      typeName: purchase.type === 'customer-retail' ? 'RETURN' : 'SALE',
      status: str(purchase.status),
      isComplete: purchase.status === 'complete',
      date,
      dateString: date.toLocaleString(),
      customerName: str(purchase.customerName),
      userName: str(purchase.userName),
      notes: Array.isArray(purchase.note) ? purchase.note.map((n: any) => str(n)) : [],
    },
    items,
    discounts,
    totals: {
      subtotal: Number(purchase.subtotal || 0),
      discount: Number(purchase.discount || 0),
      tax: Number(purchase.taxes || 0),
      tip: Number(purchase.tip || 0),
      total: Number(purchase.total || 0),
      due: Number(purchase.due || 0),
      rounding: Number(purchase.rounding || 0),
      facilitatorTax: Number((purchase.facilitatorTax && purchase.facilitatorTax.amount) || 0),
      facilitatorTaxLabel: str(purchase.facilitatorTax && purchase.facilitatorTax.label),
    },
    itemCount: countReceiptItems(purchase.items, input.getAccount),
    showItemCount: settings.showItemCount === true,
    payments,
    hasTransactions: transactions.length > 0,
    fulfillments: (purchase.fulfillments || []).map((f: any) => ({ lastFour: str(f.lastFour), amount: Number(f.amount || 0) })),
    footer,
  };
}

// ---- rendering --------------------------------------------------------------

/**
 * EJS options every receipt template is compiled with. `<%= %>` must NOT
 * HTML-escape on a paper receipt ("Joe's" would print as "Joe&#39;s").
 */
export function receiptEjsOptions(): any {
  return {
    escape: (value: any) => str(value),
    rmWhitespace: false,
    _with: true,
    client: false,
    strict: false,
    async: false,
  };
}

/** Compile a template with an ejs library instance (browser build or node). */
export function compileReceiptTemplate(ejs: any, source: string): (locals: any) => string {
  return ejs.compile(str(source), receiptEjsOptions());
}

/**
 * Normalize rendered text for the printer: LF line endings, no trailing
 * spaces, no line wider than the paper (wrapped by word, else by character —
 * Electron clips overlong lines where the native apps wrap them), and never
 * more than two consecutive blank lines.
 */
export function finalizeReceiptText(text: string, width: number = RECEIPT_DEFAULT_WIDTH): string {
  const lines: string[] = [];
  for (const rawLine of str(text).replace(/\r\n?/g, '\n').split('\n')) {
    const line = rawLine.replace(/\s+$/, '');
    if (line.length <= width) {
      lines.push(line);
    } else {
      lines.push(...wrapText(line, width));
    }
  }

  const out: string[] = [];
  let blankRun = 0;
  for (const line of lines) {
    if (line.length === 0) {
      blankRun++;
      if (blankRun > 2) {
        continue;
      }
    } else {
      blankRun = 0;
    }
    out.push(line);
  }

  // Leading/trailing blank runs are paper for nothing.
  while (out.length > 0 && out[out.length - 1].length === 0) {
    out.pop();
  }
  while (out.length > 1 && out[0].length === 0 && out[1].length === 0) {
    out.shift();
  }

  return out.join('\n');
}

export function buildReceiptLocals(model: ReceiptModel, input: ReceiptModelInput): any {
  return {
    receipt: model,
    h: createReceiptHelpers(model.width),
    purchase: input.purchase,
    location: input.location,
    client: input.client || null,
    user: input.user || null,
  };
}

/** Render a compiled template against purchase data → finalized receipt text. */
export function renderReceipt(compiled: (locals: any) => string, input: ReceiptModelInput): string {
  const model = buildReceiptModel(input);
  return finalizeReceiptText(compiled(buildReceiptLocals(model, input)), model.width);
}
